import React from 'react'
import { useNavigate, useRouter } from '@tanstack/react-router'
import * as Sentry from '@sentry/react'
import { Button } from '@/components/ui/button'

interface ErrorFallbackProps {
  error: Error
  resetError: () => void
  eventId: string | null
}

function ErrorFallback({ error, resetError, eventId }: ErrorFallbackProps) {
  const navigate = useNavigate()
  const { history } = useRouter()

  const handleReportFeedback = () => {
    if (eventId) {
      Sentry.showReportDialog({ eventId })
    }
  }

  const handleRefresh = () => {
    resetError()
    window.location.reload()
  }

  return (
    <div className='bg-background flex min-h-screen w-full items-center justify-center'>
      <div className='mx-auto max-w-md space-y-6 p-6 text-center'>
        <div className='space-y-2'>
          <h1 className='font-heading text-foreground text-4xl'>Oops!</h1>
          <p className='text-muted-foreground text-lg'>
            Something went wrong and we couldn't recover.
          </p>
        </div>

        <div className='text-muted-foreground bg-muted rounded-md p-3 text-sm'>
          <p className='mb-1 font-medium'>Error Details:</p>
          <p className='font-mono text-xs break-all'>
            {error.message || 'An unexpected error occurred'}
          </p>
        </div>

        <div className='flex flex-col justify-center gap-3 sm:flex-row'>
          <Button onClick={handleRefresh} className='min-w-[120px]'>
            Try Again
          </Button>

          <Button
            variant='outline'
            onClick={() => history.go(-1)}
            className='min-w-[120px]'
          >
            Go Back
          </Button>

          <Button
            variant='outline'
            onClick={() => navigate({ to: '/' })}
            className='min-w-[120px]'
          >
            Home
          </Button>
        </div>

        {/* Show report button if Sentry is active and we have an event ID */}
        {eventId && (
          <div className='border-border border-t pt-4'>
            <p className='text-muted-foreground mb-3 text-sm'>
              The error has already been reported to the team. You can help us
              by providing more details.
            </p>
            <Button
              variant='ghost'
              size='sm'
              onClick={handleReportFeedback}
              className='text-muted-foreground hover:text-foreground'
            >
              Report Issue
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

interface SentryErrorBoundaryProps {
  children: React.ReactNode
  fallback?: React.ComponentType<ErrorFallbackProps>
}

export function SentryErrorBoundary({
  children,
  fallback: FallbackComponent = ErrorFallback,
}: SentryErrorBoundaryProps) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError, eventId }) => (
        <FallbackComponent
          error={error as Error}
          resetError={resetError}
          eventId={eventId}
        />
      )}
      showDialog={false} // We handle the dialog manually in the fallback component
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}

export { ErrorFallback }
